import * as XLSX from 'xlsx';
import { getGstPurchases } from './data';
import { causeLabel } from './causeLabels';
import type { GstPurchaseRow } from './types';

/**
 * Excel refuses a sheet name over 31 characters or containing : \ / ? * [ ], and silently
 * corrupts the file rather than warning. Several causes are longer than that
 * ("Never booked — Finance had flagged it" is 37), so every name is trimmed and de-duplicated.
 */
const sheetName = (label: string, taken: Set<string>) => {
	const base = label.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31).trim() || 'Sheet';
	let name = base;
	for (let i = 2; taken.has(name); i++) name = `${base.slice(0, 28)} ${i}`;
	taken.add(name);
	return name;
};

/** Rows carry no category until someone investigates them; the API reports those under 'none'. */
const categoryOf = (r: GstPurchaseRow) => r.finding?.category ?? 'none';

const ROW_COLUMNS: Array<{ title: string; get: (r: GstPurchaseRow) => string | number | null }> = [
	{ title: 'Invoice #', get: r => r.gstr2b?.invoice_number ?? null },
	{ title: 'Party', get: r => r.gstr2b?.supplier_name ?? null },
	{ title: 'GSTIN', get: r => r.gstr2b?.supplier_gstin ?? null },
	{ title: 'Invoice date', get: r => r.gstr2b?.invoice_date?.slice(0, 10) ?? null },
	{ title: 'Return period', get: r => r.return_period ?? null },
	{ title: '2B value', get: r => r.gstr2b?.invoice_value ?? null },
	{ title: 'Taxable', get: r => r.gstr2b?.taxable_value ?? null },
	{ title: 'IGST', get: r => r.gstr2b?.igst ?? null },
	{ title: 'CGST', get: r => r.gstr2b?.cgst ?? null },
	{ title: 'SGST', get: r => r.gstr2b?.sgst ?? null },
	{ title: 'Cause', get: r => causeLabel(categoryOf(r)) },
	/* Row-level, and safe to sum — unlike finding.reported_rs / explained_rs, which repeat on
	   every row of a vendor and over-count the moment a column is totalled in Excel. */
	{ title: 'Still owed', get: r => r.liable_rs ?? null },
	{ title: 'Cancelled / already booked', get: r => r.nullified_rs ?? null },
	{ title: 'Basis', get: r => r.basis ?? null },
	{ title: 'GSTIN we hold', get: r => r.zoho_bill?.supplier_gstin ?? null },
	{ title: 'GSTIN matches', get: r => (r.zoho_bill ? String(r.zoho_bill.gstin_matches) : null) },
	{ title: 'Finding status', get: r => r.finding?.status ?? 'not investigated' },
	{ title: 'Reported ₹', get: r => r.finding?.reported_rs ?? null },
	{ title: 'Explained ₹', get: r => r.finding?.explained_rs ?? null },
	{ title: 'Evidence lines', get: r => r.finding?.evidence_lines ?? null },
	{ title: 'Headline', get: r => r.finding?.headline ?? null },
	{ title: 'Why', get: r => r.reason ?? null },
];

const autoWidth = (aoa: Array<Array<string | number | null>>) =>
	(aoa[0] ?? []).map((_, c) => ({
		wch: Math.min(
			Math.max(...aoa.map(row => String(row[c] ?? '').length), 10) + 2,
			60
		),
	}));

const sheetFrom = (aoa: Array<Array<string | number | null>>) => {
	const ws = XLSX.utils.aoa_to_sheet(aoa);
	ws['!cols'] = autoWidth(aoa);
	return ws;
};

/**
 * One workbook, one sheet per cause, plus a summary sheet that adds up to the bucket total.
 *
 * Fetched in a single `pageSize=all` call and grouped here rather than one request per cause:
 * every row already carries its own `finding.category`, so 19 round trips would return exactly
 * the same 1,428 rows split up, and any row whose cause changed between calls would be counted
 * twice or not at all.
 */
export async function exportGstCausesWorkbook(params: {
	status?: string;
	search?: string;
	period?: string;
	vendor?: string;
}): Promise<void> {
	const res = await getGstPurchases({ ...params, pageSize: 'all' });
	const rows = res.data as GstPurchaseRow[];

	const groups = new Map<string, GstPurchaseRow[]>();
	for (const r of rows) {
		const c = categoryOf(r);
		if (!groups.has(c)) groups.set(c, []);
		groups.get(c)!.push(r);
	}

	// Biggest money first — the same order the on-screen breakdown uses, so the sheet tabs read
	// in the order someone already has in their head.
	const valueOf = (list: GstPurchaseRow[]) =>
		list.reduce((t, r) => t + (Number(r.gstr2b?.invoice_value) || 0), 0);
	const liableOf = (list: GstPurchaseRow[]) => list.reduce((t, r) => t + (r.liable_rs ?? 0), 0);
	const ordered = [...groups.entries()].sort((a, b) => valueOf(b[1]) - valueOf(a[1]));

	const wb = XLSX.utils.book_new();
	const taken = new Set<string>();

	const summary: Array<Array<string | number | null>> = [
		['Cause', 'Rows', '2B value', 'Still owed', 'Signed off', 'Sheet'],
	];
	const sheets: Array<{ name: string; aoa: Array<Array<string | number | null>> }> = [];

	for (const [cat, list] of ordered) {
		const label = causeLabel(cat);
		const name = sheetName(label, taken);
		summary.push([
			label,
			list.length,
			valueOf(list),
			liableOf(list),
			list.filter(r => r.finding?.status === 'reviewed_ok').length,
			name,
		]);
		sheets.push({
			name,
			aoa: [ROW_COLUMNS.map(c => c.title), ...list.map(r => ROW_COLUMNS.map(c => c.get(r)))],
		});
	}
	summary.push([
		'Total',
		rows.length,
		valueOf(rows),
		liableOf(rows),
		rows.filter(r => r.finding?.status === 'reviewed_ok').length,
		'',
	]);

	// Summary first so the workbook opens on the overview, not on whichever cause sorted highest.
	XLSX.utils.book_append_sheet(wb, sheetFrom(summary), 'Summary');
	for (const s of sheets) XLSX.utils.book_append_sheet(wb, sheetFrom(s.aoa), s.name);

	const parts = ['gst_causes', params.period, params.vendor].filter(Boolean);
	XLSX.writeFile(wb, `${parts.join('_')}.xlsx`);
}
